/**
 * **Who a queue item is about, and the one line that says what it is** — the two lines the Pulpit's
 * „Zadania" rows draw beside their type tile. „Szybki feedback" drew them too until its rows became
 * the short Talia card (24 September 2026), whose head says who and what itself.
 *
 * ── Both lines are clipped, and both carry the whole string as a `title` ─────────────────────
 * The clip is CSS, so the full name and the full sentence are still in the accessibility tree — the
 * reader who loses anything is the one looking at a pointer, and the `title` is what answers them.
 */

import { useTranslation } from 'react-i18next';

import skel from '~/shared/ui/skeleton.module.css';

import type { AttentionItem } from '../model/attentionItems';
import styles from './ItemLines.module.css';

interface ItemLinesProps {
  readonly item: AttentionItem;
}

export function ItemLines({ item }: ItemLinesProps) {
  const { t } = useTranslation();
  // Mobile's own sentence for this reason, chosen among that reason's variants through an i18next
  // fallback chain. `model/attentionSections` decides the chain; nothing is worded here.
  const line = t([...item.description.keys], item.description.values);

  return (
    <span className={styles.lines}>
      <span className={styles.name} title={item.fullName}>
        {item.fullName}
      </span>
      <span className={styles.line} title={line}>
        {line}
      </span>
    </span>
  );
}

/**
 * The same two lines, WAITING.
 *
 * It lives here rather than in either card for the reason the lines themselves do: the reserve has
 * to be the shape of the answer, and the shape is this file's — a task row's sentence is a two-line
 * box, so a card that drew its own bars would have to know that and would go on knowing it after
 * this file changed its mind. It is also what the two cards were
 * reaching into `ItemLines.module.css` for, across a slice boundary the barrel is supposed to be.
 *
 * The GATE is the caller's: `shared/lib/useSkeletonGate` hides the bars, never the box, so this
 * element holds its height whether `skeleton` is true or false.
 */
export function ItemLinesReserve({ skeleton }: { readonly skeleton: boolean }) {
  const bar = (className: string) => `${className} ${skeleton ? String(skel.bar) : ''}`;
  return (
    <span className={styles.lines}>
      <span className={bar(String(styles.barName))} />
      <span className={bar(String(styles.barLine))} />
      {/* TWO lines of sentence are reserved, as for the real thing. The second bar is shorter, the
        * way a wrapped sentence ends. */}
      <span className={bar(String(styles.barLineB))} />
    </span>
  );
}
